import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { mutations } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';

import { Dialog, showSnackbar } from 'components/shared';
import ControlledDatePicker from 'components/shared/ControlledFields/ControlledDatePicker';

import connectSelfMatchDutiesSchema from './connect-selfmatch-duties-schema';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

const schema = connectSelfMatchDutiesSchema();
type Form = InferType<typeof schema>;

type Props = {
  open?: true;
  organizationalUnitIds: number[];
  startDate?: string;
  endDate?: string;
  onHidden: () => void;
};

export default function ConnectSelfMatchDutiesDialog({
  open = true,
  onHidden,
  organizationalUnitIds,
  startDate,
  endDate
}: Props) {
  const { t } = useTranslation('common');
  const [hide, setHide] = useState(false);

  const defaultValues = {
    startDate: dayjs(startDate),
    endDate: dayjs(endDate).subtract(1, 'day')
  };

  const { control, handleSubmit } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { mutate: connectSelfMatchDuties, isLoading } =
    mutations.useConnectSelfMatchDuties();

  const handleClose = () => {
    setHide(true);
  };

  async function onSubmit(data) {
    const { startDate, endDate } = data;
    const payload = {
      organizationalUnitIds,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };

    await connectSelfMatchDuties(payload, {
      onSuccess: () => {
        handleClose();
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    });
  }

  return (
    <Dialog
      title={t('connectSelfMatchDuties')}
      handleClose={handleClose}
      TransitionProps={{ onExited: onHidden }}
      open={hide ? false : open}
      maxWidth="medium"
    >
      <div style={{ paddingBottom: '20px' }}>
        {t('connectSelfMatchDutiesExactOneSubscriber')}
      </div>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <GridContainer>
          <GridCompact6>
            <ControlledDatePicker
              name="startDate"
              control={control}
              label={t('startDate')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledDatePicker
              name="endDate"
              control={control}
              label={t('endDate')}
            />
          </GridCompact6>
        </GridContainer>
        <DialogActions sx={{ mt: 3 }}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={() => {
              handleClose();
            }}
          >
            {t('cancel')}
          </Button>
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            color="primary"
          >
            {t('connect')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
